import { lazy, Suspense, type ComponentType, type ReactElement } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import AuthGuard from "@/components/AuthGuard";
import ErrorBoundary from "@/components/ErrorBoundary";
import NotFound from "@/components/NotFound";
import RoleGuard from "@/components/RoleGuard";
import { CardSkeleton, DetailSkeleton, TableSkeleton } from "@/components/LoadingSkeletons";
import DashboardLayout from "@/layouts/DashboardLayout";
import { loadWithReload } from "@/lib/lazyWithReload";
import SaseDoor, { type SaseDoorModule } from "@/features/sase/SaseDoor";
import LoginPage from "@/features/auth/LoginPage";
import SsoCallbackPage from "@/features/auth/SsoCallbackPage";
import RegisterPage from "@/features/auth/RegisterPage";
import ForgotPasswordPage from "@/features/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/features/auth/ResetPasswordPage";
import VerifyEmailPage from "@/features/auth/VerifyEmailPage";

/**
 * Suspense fallback per page shape, so a loading detail page doesn't flash a
 * fake table (and vice versa): "table" for list pages, "detail" for `/:id`
 * pages, "card" for stat/chart pages (dashboard, billing, capacity).
 */
export type FallbackKind = "table" | "detail" | "card";

const FALLBACKS: Record<FallbackKind, ReactElement> = {
  table: <TableSkeleton />,
  detail: <DetailSkeleton />,
  card: <CardSkeleton />,
};

// Every feature page is code-split: the main chunk carries only the shell
// (layout, guards, login). Each page loads on first navigation and is then
// cached by the browser. Keep new routes lazy. A page whose code is gone
// (a tab opened before a deploy) reloads the tab once: `loadWithReload`.
// A page that still cannot load after that reload fails inside the layout,
// with a Reload button, instead of replacing the whole console with the
// router's error screen. Keyed per page: one page's failure is not carried to
// the next one opened.
let pageSeq = 0;

const lazyPage = (
  load: () => Promise<{ default: ComponentType }>,
  fallback: FallbackKind = "table",
) => {
  const Page = lazy(loadWithReload(load));
  return (
    <ErrorBoundary key={`page-${(pageSeq += 1)}`}>
      <Suspense fallback={FALLBACKS[fallback]}>
        <Page />
      </Suspense>
    </ErrorBoundary>
  );
};

/** A page of a module the SASE section shares with the cloud, mounted in its
 *  SASE door (SD-01, SASE-18): the same lazy module, behind `sase` AND the
 *  module's flag, with its links built from the SASE path prefix. The door
 *  sits inside the Suspense so the page keeps its own skeleton. */
const lazyDoor = (
  module: SaseDoorModule,
  load: () => Promise<{ default: ComponentType }>,
  fallback: FallbackKind = "table",
) => {
  const Page = lazy(loadWithReload(load));
  return (
    <ErrorBoundary key={`page-${(pageSeq += 1)}`}>
      <Suspense fallback={FALLBACKS[fallback]}>
        <SaseDoor module={module}>
          <Page />
        </SaseDoor>
      </Suspense>
    </ErrorBoundary>
  );
};

const dashboard = lazyPage(() => import("@/features/dashboard/Overview"), "card");
const instances = lazyPage(() => import("@/features/compute/InstancesPage"));
const functions = lazyPage(() => import("@/features/functions/FunctionsPage"));
const functionDetail = lazyPage(() => import("@/features/functions/FunctionDetailPage"), "detail");
const queues = lazyPage(() => import("@/features/queues/QueuesPage"));
const queueDetail = lazyPage(() => import("@/features/queues/QueueDetailPage"), "detail");
const topics = lazyPage(() => import("@/features/topics/TopicsPage"));
const topicDetail = lazyPage(() => import("@/features/topics/TopicDetailPage"), "detail");
const instanceDetail = lazyPage(() => import("@/features/compute/InstanceDetailPage"), "detail");
const autoScalingGroups = lazyPage(() => import("@/features/autoscaling/AutoScalingGroupsPage"));
const scheduledActions = lazyPage(() => import("@/features/scheduledActions/ScheduledActionsPage"));
const scheduledActionDetail = lazyPage(
  () => import("@/features/scheduledActions/ScheduledActionDetailPage"),
  "detail",
);
const autoScalingGroupDetail = lazyPage(
  () => import("@/features/autoscaling/AutoScalingGroupDetailPage"),
  "detail",
);
const placementGroups = lazyPage(
  () => import("@/features/placementGroups/PlacementGroupsPage"),
);
const launchTemplates = lazyPage(
  () => import("@/features/launchTemplates/LaunchTemplatesPage"),
);
const keypairs = lazyPage(() => import("@/features/compute/KeyPairsPage"));
const account = lazyPage(() => import("@/features/account/AccountSettingsPage"), "card");
const licences = lazyPage(() => import("@/features/about/LicencesPage"), "card");
const credentials = lazyPage(() => import("@/features/credentials/CredentialsPage"));
const networks = lazyPage(() => import("@/features/networks/NetworksPage"));
const networkDetail = lazyPage(() => import("@/features/networks/NetworkDetailPage"), "detail");
const volumes = lazyPage(() => import("@/features/volumes/VolumesPage"));
const volumeDetail = lazyPage(() => import("@/features/volumes/VolumeDetailPage"), "detail");
const loadBalancers = lazyPage(() => import("@/features/lbaas/LoadBalancersPage"));
const loadBalancerDetail = lazyPage(
  () => import("@/features/lbaas/LoadBalancerDetailPage"),
  "detail",
);
const images = lazyPage(() => import("@/features/images/ImagesPage"));
const imageDetail = lazyPage(() => import("@/features/images/ImageDetailPage"), "detail");
const agentGateways = lazyPage(() => import("@/features/agentGateway/AgentGatewaysPage"));
const agentGatewayDetail = lazyPage(
  () => import("@/features/agentGateway/GatewayDetailPage"),
  "detail",
);
// AGW-47: where an AI app sends a person to approve it (signed in, like every console page)
const oauthConsent = lazyPage(() => import("@/features/agentGateway/OAuthConsentPage"));
const dns = lazyPage(() => import("@/features/dns/DnsPage"));
const zoneDetail = lazyPage(() => import("@/features/dns/ZoneDetailPage"), "detail");
const privateAccess = lazyPage(() => import("@/features/privateAccess/PrivateAccessPage"));
const signInTest = lazyPage(() => import("@/features/privateAccess/SignInTestPage"));
// PA-12: an organisation's end users, not console users: outside the console's guard.
const accessLauncher = lazyPage(() => import("@/features/accessLauncher/LauncherPage"), "card");
const accessBlocked = lazyPage(() => import("@/features/accessLauncher/BlockPage"), "card");
const accessJoin = lazyPage(() => import("@/features/accessLauncher/JoinPage"), "card");
// PA-11: the first protected app, guided
const accessGetStarted = lazyPage(() => import("@/features/privateAccess/GetStartedPage"));
// SASE (SASE-1): the section's Overview and Get started.
const saseOverview = lazyPage(() => import("@/features/sase/SaseOverviewPage"), "card");
const saseGetStarted = lazyPage(() => import("@/features/sase/SaseGetStartedPage"), "card");
// SASE-7: one policy (rule sets, objects, profiles, versions, tester) and its rule editor.
const sasePolicies = lazyPage(() => import("@/features/sase/policies/PoliciesPage"));
const saseRuleEditor = lazyPage(() => import("@/features/sase/policies/RuleEditorPage"), "detail");
// SASE-8: Internet access.
const saseInternet = lazyPage(() => import("@/features/sase/internet/InternetAccessPage"));
// SASE-6: Connections (devices, sites and tunnels, proxy, DNS); SASE-2: Settings.
const saseConnections = lazyPage(() => import("@/features/sase/connections/ConnectionsPage"));
const saseSettings = lazyPage(() => import("@/features/sase/settings/SaseSettingsPage"), "card");
// SASE-35 (P35c): Settings → Partner (a partner's customers, team and branding).
const sasePartner = lazyPage(() => import("@/features/sase/partner/PartnerPage"));
// SASE-18 (with the SASE-12/13 doors): the shared modules' SASE doors.
const sasePrivateAccess = lazyDoor(
  "privateAccess",
  () => import("@/features/privateAccess/PrivateAccessPage"),
);
const sasePrivateAccessGetStarted = lazyDoor(
  "privateAccess",
  () => import("@/features/privateAccess/GetStartedPage"),
);
const saseUsers = lazyDoor("usersAndDevices", () => import("@/features/sase/users/SaseUsersPage"));
// SASE-9/10/11: DNS protection, Firewall, Threat protection (each page gates itself).
const saseDns = lazyPage(() => import("@/features/sase/dns/DnsProtectionPage"));
const saseFirewall = lazyPage(() => import("@/features/sase/firewall/FirewallPage"));
const saseThreats = lazyPage(() => import("@/features/sase/threats/ThreatProtectionPage"));
const saseAgentGateways = lazyDoor(
  "agentGateways",
  () => import("@/features/agentGateway/AgentGatewaysPage"),
);
const saseAgentGatewayDetail = lazyDoor(
  "agentGateways",
  () => import("@/features/agentGateway/GatewayDetailPage"),
  "detail",
);
const pointsOfPresence = lazyPage(() => import("@/features/sase/pops/PointsOfPresencePage"));
// SASE-14: Analytics (overview, activity search, user and app views) and
// Incidents (threats), each behind `sase` AND its module flag.
const saseAnalytics = lazyDoor("analytics", () => import("@/features/sase/SaseAnalyticsPage"), "card");
const saseActivity = lazyDoor("analytics", () => import("@/features/sase/SaseActivityPage"));
// SASE-38 (P38): Analytics → Reports, behind the same analytics door.
const saseReports = lazyDoor("analytics", () => import("@/features/sase/reports/SaseReportsPage"));
const saseUserView = lazyDoor(
  "analytics",
  () => import("@/features/sase/SaseSubjectPage").then((m) => ({ default: m.SaseUserViewPage })),
  "detail",
);
const saseAppView = lazyDoor(
  "analytics",
  () => import("@/features/sase/SaseSubjectPage").then((m) => ({ default: m.SaseAppViewPage })),
  "detail",
);
const saseIncidents = lazyDoor("incidents", () => import("@/features/sase/SaseIncidentsPage"));
const saseCloudApps = lazyPage(() => import("@/features/sase/cloudapps/CloudAppsPage"));
// SASE-13: AI security, one page whose tabs are routes (Overview, AI apps,
// Prompt protection), behind `sase` AND the ai_security module.
// The Overview is card-shaped, AI apps and Prompt protection table-shaped.
const saseAiOverview = lazyDoor("aiSecurity", () => import("@/features/sase/ai/AiSecurityPage"), "card");
const saseAiSecurity = lazyDoor("aiSecurity", () => import("@/features/sase/ai/AiSecurityPage"));
// SASE-24 (P24a): Data protection → Exact data, behind `sase` AND data_protection.
const saseExactData = lazyDoor("dataProtection", () => import("@/features/sase/data/ExactDataPage"));
// SASE-24 (P37): Data protection → Document fingerprints, the same gate.
const saseFingerprints = lazyDoor("dataProtection", () => import("@/features/sase/data/FingerprintsPage"));
// SASE-29 (B25): Experience, behind `sase` AND the experience module.
const saseExperience = lazyDoor("experience", () => import("@/features/sase/experience/ExperiencePage"));
const vpn = lazyPage(() => import("@/features/vpn/VpnPage"));
const vpnDetail = lazyPage(() => import("@/features/vpn/VpnDetailPage"), "detail");
const acls = lazyPage(() => import("@/features/acls/AclsPage"));
const aclDetail = lazyPage(() => import("@/features/acls/AclDetailPage"), "detail");
const flowLogs = lazyPage(() => import("@/features/flow-logs/FlowLogsPage"));
const trafficMirrors = lazyPage(() => import("@/features/trafficMirrors/TrafficMirrorsPage"));
// Content delivery (plan CDN-4): the page gates itself on the capability.
const distributions = lazyPage(() => import("@/features/cdn/DistributionsPage"));
const distributionDetail = lazyPage(
  () => import("@/features/cdn/DistributionDetailPage"),
  "detail",
);
// Video streaming (plan VID-7): each page gates itself on media.video_streaming.
const videos = lazyPage(() => import("@/features/video/VideosPage"));
const videoDetail = lazyPage(() => import("@/features/video/VideoDetailPage"), "detail");
const videoSigningKeys = lazyPage(() => import("@/features/video/SigningKeysPage"));
const videoSettings = lazyPage(() => import("@/features/video/VideoSettingsPage"));
const shares = lazyPage(() => import("@/features/shares/SharesPage"));
const shareDetail = lazyPage(() => import("@/features/shares/ShareDetailPage"), "detail");
const orchestration = lazyPage(() => import("@/features/orchestration/OrchestrationPage"));
const stackDetail = lazyPage(() => import("@/features/orchestration/StackDetailPage"), "detail");
const secrets = lazyPage(() => import("@/features/secrets/SecretsPage"));
const secretDetail = lazyPage(() => import("@/features/secrets/SecretDetailPage"), "detail");
const objects = lazyPage(() => import("@/features/objects/ObjectsPage"));
const bucketDetail = lazyPage(() => import("@/features/objects/BucketDetailPage"), "detail");
const database = lazyPage(() => import("@/features/database/DatabasePage"));
const databaseDetail = lazyPage(() => import("@/features/database/DatabaseDetailPage"), "detail");
const parameterGroups = lazyPage(() => import("@/features/database/ParameterGroupsPage"));
const k8sClusters = lazyPage(() => import("@/features/k8s/K8sClustersPage"));
const k8sClusterDetail = lazyPage(() => import("@/features/k8s/K8sClusterDetailPage"), "detail");
const identity = lazyPage(() => import("@/features/identity/IdentityPage"));
const machines = lazyPage(() => import("@/features/machines/MachinesPage"));
const maintenance = lazyPage(() => import("@/features/maintenance/MaintenancePage"));
const agentGatewayFleet = lazyPage(
  () => import("@/features/agentGatewayFleet/AgentGatewayFleetPage"),
);
const optimization = lazyPage(() => import("@/features/optimization/OptimizationPage"));
const actionPlanDetail = lazyPage(
  () => import("@/features/optimization/ActionPlanDetailPage"),
  "detail",
);
const logs = lazyPage(() => import("@/features/logs/LogsPage"));
const billing = lazyPage(() => import("@/features/billing/BillingPage"), "card");
const audit = lazyPage(() => import("@/features/audit/AuditPage"));
const monitoring = lazyPage(() => import("@/features/monitoring/MonitoringPage"), "card");
const alarms = lazyPage(() => import("@/features/alarms/AlarmsPage"));

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  // SASE-30: the identity provider's redirect after a company sign-in.
  {
    path: "/login/sso",
    element: <SsoCallbackPage />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/verify-email",
    element: <VerifyEmailPage />,
  },
  { path: "/launch/:slug", element: accessLauncher },
  { path: "/launch/:slug/blocked", element: accessBlocked },
  { path: "/launch/:slug/join", element: accessJoin },
  {
    path: "/",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      { index: true, element: dashboard },
      { path: "compute", element: instances },
      // Declared before `compute/:id` so the literal segment wins.
      { path: "compute/autoscaling-groups", element: autoScalingGroups },
      { path: "compute/autoscaling-groups/:id", element: autoScalingGroupDetail },
      { path: "compute/scheduled-actions", element: scheduledActions },
      { path: "compute/scheduled-actions/:id", element: scheduledActionDetail },
      { path: "compute/placement-groups", element: placementGroups },
      { path: "compute/launch-templates", element: launchTemplates },
      { path: "compute/:id", element: instanceDetail },
      { path: "keypairs", element: keypairs },
      { path: "account", element: account },
      { path: "about/licences", element: licences },
      { path: "credentials", element: credentials },
      { path: "network", element: networks },
      { path: "network/:id", element: networkDetail },
      { path: "storage", element: volumes },
      { path: "storage/:id", element: volumeDetail },
      { path: "lbaas", element: loadBalancers },
      { path: "lbaas/:id", element: loadBalancerDetail },
      { path: "images", element: images },
      { path: "images/:id", element: imageDetail },
      { path: "dns", element: dns },
      { path: "dns/:id", element: zoneDetail },
      { path: "access", element: privateAccess },
      { path: "access/sign-in-test", element: signInTest },
      { path: "access/get-started", element: accessGetStarted },
      { path: "sase", element: saseOverview },
      { path: "sase/get-started", element: saseGetStarted },
      { path: "sase/policies", element: sasePolicies },
      { path: "sase/policies/rules/:ruleId", element: saseRuleEditor },
      { path: "sase/connections", element: saseConnections },
      { path: "sase/internet", element: saseInternet },
      { path: "sase/private-access", element: sasePrivateAccess },
      { path: "sase/cloud-apps", element: saseCloudApps },
      { path: "sase/private-access/get-started", element: sasePrivateAccessGetStarted },
      { path: "sase/users", element: saseUsers },
      { path: "sase/experience", element: saseExperience },
      { path: "sase/dns", element: saseDns },
      { path: "sase/firewall", element: saseFirewall },
      { path: "sase/threats", element: saseThreats },
      { path: "sase/ai", element: saseAiOverview },
      { path: "sase/ai/apps", element: saseAiSecurity },
      { path: "sase/ai/prompt-protection", element: saseAiSecurity },
      { path: "sase/ai/conversations", element: saseAiSecurity },
      { path: "sase/ai/mcp-servers", element: saseAiSecurity },
      { path: "sase/data/exact", element: saseExactData },
      { path: "sase/data/fingerprints", element: saseFingerprints },
      { path: "sase/ai/agent-gateways", element: saseAgentGateways },
      { path: "sase/ai/agent-gateways/:id", element: saseAgentGatewayDetail },
      { path: "sase/incidents", element: saseIncidents },
      { path: "sase/analytics", element: saseAnalytics },
      { path: "sase/analytics/activity", element: saseActivity },
      { path: "sase/analytics/reports", element: saseReports },
      { path: "sase/analytics/users/:user", element: saseUserView },
      { path: "sase/analytics/apps/:app", element: saseAppView },
      { path: "sase/settings", element: saseSettings },
      { path: "sase/settings/partner", element: sasePartner },
      { path: "vpn", element: vpn },
      { path: "vpn/:id", element: vpnDetail },
      { path: "network-acls", element: acls },
      { path: "network-acls/:id", element: aclDetail },
      { path: "flow-logs", element: flowLogs },
      { path: "traffic-mirrors", element: trafficMirrors },
      { path: "content-delivery", element: distributions },
      { path: "content-delivery/:id", element: distributionDetail },
      { path: "video-streaming", element: videos },
      { path: "video-streaming/signing-keys", element: videoSigningKeys },
      { path: "video-streaming/settings", element: videoSettings },
      { path: "video-streaming/:id", element: videoDetail },
      { path: "shares", element: shares },
      { path: "shares/:id", element: shareDetail },
      { path: "orchestration", element: orchestration },
      { path: "orchestration/:id", element: stackDetail },
      { path: "secrets", element: secrets },
      { path: "secrets/:id", element: secretDetail },
      { path: "objects", element: objects },
      { path: "objects/:bucket", element: bucketDetail },
      { path: "database", element: database },
      { path: "database/parameter-groups", element: parameterGroups },
      { path: "database/:id", element: databaseDetail },
      { path: "functions", element: functions },
      { path: "functions/:name", element: functionDetail },
      { path: "agent-gateways", element: agentGateways },
      { path: "agent-gateways/:id", element: agentGatewayDetail },
      { path: "oauth/consent", element: oauthConsent },
      { path: "queues", element: queues },
      { path: "queues/:id", element: queueDetail },
      { path: "topics", element: topics },
      { path: "topics/:id", element: topicDetail },
      { path: "k8s", element: k8sClusters },
      { path: "k8s/:id", element: k8sClusterDetail },
      {
        path: "identity",
        element: <RoleGuard>{identity}</RoleGuard>,
      },
      {
        // Compute capacity became a tab of Machines (2026-09-05). Keep the old
        // path working: it was in the admin nav for months and will be
        // bookmarked.
        path: "fleet",
        element: <Navigate to="/machines" replace />,
      },
      {
        path: "machines",
        element: <RoleGuard>{machines}</RoleGuard>,
      },
      {
        path: "maintenance",
        element: <RoleGuard>{maintenance}</RoleGuard>,
      },
      {
        // Phase 7: where agent gateways and their agents run (operator only)
        path: "agent-gateway-fleet",
        element: <RoleGuard>{agentGatewayFleet}</RoleGuard>,
      },
      {
        path: "points-of-presence",
        element: <RoleGuard>{pointsOfPresence}</RoleGuard>,
      },
      {
        path: "optimization",
        element: <RoleGuard>{optimization}</RoleGuard>,
      },
      {
        path: "optimization/action-plans/:id",
        element: <RoleGuard>{actionPlanDetail}</RoleGuard>,
      },
      { path: "alarms", element: alarms },
      { path: "billing", element: billing },
      // Audit is customer-visible: the backend force-scopes non-admin reads
      // to the caller's own project (endpoints/audit.py `_scope_for`), so a
      // member sees exactly their tenant's activity — no RoleGuard (F14).
      { path: "audit", element: audit },
      {
        path: "monitoring",
        element: <RoleGuard>{monitoring}</RoleGuard>,
      },
      {
        path: "logs",
        element: <RoleGuard>{logs}</RoleGuard>,
      },
      // Unknown route inside the console: a real 404 page in the shell, not
      // a silent bounce to the dashboard.
      { path: "*", element: <NotFound /> },
    ],
  },
  {
    // Fallback for anything outside the authenticated layout.
    path: "*",
    element: <NotFound />,
  },
]);
